import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import MenuItem from '@material-ui/core/MenuItem';
import React from 'react';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import { isValidDomain } from 'utils/validators';
import { withFormStyles } from '../styles';

const SCOPE_OPTIONS = {
    page: 'This page only',
    domain: 'Entire domain',
    page_sub_pages: 'This page and all sub-pages',
    not_page_sub_pages: 'Not this page but all sub-pages',
    not_page: 'Not this page',
};

const RESTRICTION_OPTIONS = {
    0: 'All',
    1: 'Auto-save',
    2: 'Auto-fill',
};

const DEFAULT_SCOPE = 'page';
const DEFAULT_RESTRICTION = 0;

class ExcludeSitesForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = this.init();
    }

    init() {
        return {
            values: this.getValues(this.props.policy.value.policy),
            errors: {},
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.policy.value.policy !== this.props.policy.value.policy) {
            this.setState(this.init());
        }
    }

    handleChange = key => name => event => {
        const { value } = event.target;
        this.setState((state, props) => {
            const values = state.values.map((item, i) => {
                if (i === key) {
                    return { ...item, [name]: value };
                }
                return item;
            });
            return { values, errors: {} };
        });
    };

    handleDelete = key => event => {
        this.setState((state, props) => {
            const values = state.values.filter((_, i) => {
                if (i === key) {
                    return false;
                }
                return true;
            });
            return { values, errors: {} };
        });
    };

    handleCancel = () => {
        this.setState(this.init());
    };

    handleAdd = () => {
        this.setState(state => {
            const newValue = {
                uri: '',
                scope: DEFAULT_SCOPE,
                restriction: DEFAULT_RESTRICTION,
            };
            const values = state.values.concat([newValue]);
            return { values };
        });
    };

    validate() {
        const { values } = this.state;
        return values
            .map(({ uri, scope, restriction }, key) => {
                return {
                    key,
                    uri: !isValidDomain(uri),
                    scope: !scope,
                    restriction:
                        restriction === undefined || restriction === null,
                };
            })
            .filter(({ uri, scope, restriction }) => {
                return uri || scope || restriction;
            })
            .reduce((acc, { uri, scope, restriction, key }) => {
                acc[key] = {
                    uri,
                    scope,
                    restriction,
                };
                return acc;
            }, {});
    }

    handleSave = () => {
        const errors = this.validate();
        this.setState({ errors }, () => {
            if (Object.keys(this.state.errors).length === 0) {
                const value = this.state.values.map(
                    ({ uri, scope, restriction }) => ({
                        uri,
                        scope,
                        restriction,
                    })
                );
                const data = {
                    uuid: this.props.policy.uuid,
                    enabled: true,
                    value,
                };
                this.props.onSave(data);
            }
        });
    };

    getValues = value => {
        if (!value.length) {
            return [
                {
                    uri: '',
                    scope: DEFAULT_SCOPE,
                    restriction: DEFAULT_RESTRICTION,
                },
            ];
        }
        return value.map(
            ({
                uri,
                scope = DEFAULT_SCOPE,
                restriction = DEFAULT_RESTRICTION,
            }) => {
                return {
                    uri,
                    scope,
                    restriction,
                };
            }
        );
    };
    hasError = key => name => {
        return this.state.errors[key] && this.state.errors[key][name];
    };
    render() {
        const { classes, enabled, editable } = this.props;
        const { values } = this.state;
        const isNotEmpty = values.length > 1;
        const disabled = !enabled || !editable;
        return (
            <>
                <div className={classes.excludeSitesInputs}>
                    <Typography>
                        CyberFOX Password Manager will be disabled on these
                        pages and sites:
                    </Typography>
                    {values.map((value, key) => (
                        <LineItem
                            key={key}
                            value={value}
                            enabled={!disabled}
                            isNotEmpty={isNotEmpty}
                            onChange={this.handleChange(key)}
                            onDelete={this.handleDelete(key)}
                            hasError={this.hasError(key)}
                            className={classes.excludeSitesLine}
                        />
                    ))}
                </div>
                <div className={classes.excludeSitesActions}>
                    <Button
                        size="small"
                        disabled={disabled}
                        variant="contained"
                        className={classes.button}
                        onClick={this.handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="small"
                        disabled={disabled}
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        onClick={this.handleAdd}
                    >
                        Add
                    </Button>
                    <Button
                        size="small"
                        disabled={disabled}
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        onClick={this.handleSave}
                    >
                        Save
                    </Button>
                </div>
            </>
        );
    }
}

function LineItem({
    value,
    enabled,
    onChange,
    onDelete,
    isNotEmpty,
    hasError,
    className,
}) {
    const { uri, scope, restriction } = value;
    return (
        <div className={className}>
            <div className="row">
                <Input
                    value={uri}
                    placeholder="domain.com"
                    error={hasError('uri')}
                    onChange={onChange('uri')}
                    disabled={!enabled}
                    fullWidth
                />
            </div>
            <div className="row">
                <Select
                    error={hasError('scope')}
                    value={scope}
                    onChange={onChange('scope')}
                    disabled={!enabled}
                    fullWidth
                >
                    {Object.keys(SCOPE_OPTIONS).map(name => (
                        <MenuItem key={name} value={name}>
                            {SCOPE_OPTIONS[name]}
                        </MenuItem>
                    ))}
                </Select>
            </div>
            <div className="row">
                <Select
                    error={hasError('restriction')}
                    value={restriction}
                    onChange={onChange('restriction')}
                    disabled={!enabled}
                    fullWidth
                >
                    {Object.keys(RESTRICTION_OPTIONS).map(key => (
                        <MenuItem key={key} value={Number(key)}>
                            {RESTRICTION_OPTIONS[key]}
                        </MenuItem>
                    ))}
                </Select>
            </div>
            {isNotEmpty && (
                <ListItemSecondaryAction>
                    <IconButton
                        aria-label="Delete"
                        onClick={onDelete}
                        disabled={!enabled}
                    >
                        <DeleteIcon />
                    </IconButton>
                </ListItemSecondaryAction>
            )}
        </div>
    );
}

export default withFormStyles(ExcludeSitesForm);
